/**
 * Academy Video Coordinator
 * -------------------------------------------------------------------------
 * Mobile browsers have very few hardware video decoders (often 1–3). Pages
 * like the upGrade landing page carry 30+ autoplay <video> loops across
 * their sections; when too many run at once the decoder pool is exhausted
 * and playback stalls or renders a single YUV plane ("only one colour
 * channel"). Each section keeps its own play/pause logic — this is a
 * global cap on top of it: at most MAX videos ever play simultaneously,
 * always the most-visible ones. The rest are paused (their poster/last
 * frame stays), and resume automatically once they climb back into the
 * visible top-MAX as the section's own logic re-.play()s them.
 *
 * Purely reactive to native 'play' / 'pause' events (capture phase) plus
 * an IntersectionObserver for the visibility ranking — no monkey-patching,
 * no ownership of playback, safe to drop in alongside everything else.
 *
 * Also immediately re-pauses any video that starts playing while
 * `display: none` (see the 'play' handler below) — desktop/mobile background
 * pairs render both elements and hide the off-breakpoint one via CSS, but
 * neither native `autoplay` nor a section's own `.play()` call checks that.
 */
(function () {
  'use strict';
  if (window.__academyVideoCoordinator) return;
  window.__academyVideoCoordinator = true;

  function isMobile() {
    return !!(window.matchMedia && window.matchMedia('(max-width: 767px)').matches);
  }
  var MAX = isMobile() ? 2 : 4;

  var ratios  = new WeakMap(); // video -> current intersectionRatio (0 when off-screen)
  var playing = new Set();      // videos currently in `playing` state
  var raf = 0;

  function ratioOf(v) { return ratios.get(v) || 0; }

  function reconcile() {
    raf = 0;
    if (playing.size <= MAX) return;
    var arr = Array.prototype.slice.call(playing).sort(function (a, b) {
      return ratioOf(a) - ratioOf(b); // least visible first
    });
    while (arr.length > MAX) {
      var v = arr.shift();
      try { v.pause(); } catch (e) {}
    }
  }
  function schedule() {
    if (raf || !playing.size) return;
    raf = requestAnimationFrame(reconcile);
  }

  var io = ('IntersectionObserver' in window)
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          ratios.set(e.target, e.isIntersecting ? e.intersectionRatio : 0);
        });
        schedule();
      }, { threshold: [0, 0.15, 0.35, 0.6, 0.85, 1] })
    : null;

  function track(v) {
    if (v.__vcTracked) return;
    v.__vcTracked = true;
    if (io) io.observe(v);
  }
  // Theme editor: sections get swapped via AJAX (shopify:section:load) and
  // the device-preview toggle resizes the preview iframe — both can leave
  // offsetParent transiently/incorrectly null for a video that's actually
  // meant to be visible, permanently pausing it there with nothing left to
  // resume it. The offsetParent-based display:none guard below is a
  // storefront-only optimization, so skip it entirely in the editor.
  var isDesignMode = typeof Shopify !== 'undefined' && Shopify.designMode;

  function scan(root) {
    var list = (root || document).querySelectorAll ? (root || document).querySelectorAll('video') : [];
    Array.prototype.forEach.call(list, track);
    if (isDesignMode) return;
    // Native `autoplay` can start a video (and fire its 'play' event) before
    // this deferred script has even run — the capture-phase listener below
    // never sees that first event, so catch anything already playing
    // display:none here too.
    Array.prototype.forEach.call(list, function (v) {
      if (!v.paused && v.offsetParent === null) v.pause();
    });
  }

  document.addEventListener('play', function (e) {
    var v = e.target;
    if (!v || v.tagName !== 'VIDEO') return;
    // Desktop/mobile background-video pairs (hero, upgrade-hero, bento, …)
    // render BOTH elements and hide the non-matching one via CSS
    // `display: none` at a breakpoint — but native `autoplay` and any JS
    // `.play()` call ignore that and start decoding it anyway. offsetParent
    // is null exactly when the element (or an ancestor) is display:none, so
    // this immediately stops that one instead of letting it decode
    // invisibly for the rest of the page's lifetime. Skipped in the theme
    // editor — see isDesignMode above.
    if (!isDesignMode && v.offsetParent === null) {
      v.pause();
      return;
    }
    track(v);
    playing.add(v);
    schedule();
  }, true);
  document.addEventListener('pause', function (e) {
    if (e.target && e.target.tagName === 'VIDEO') playing.delete(e.target);
  }, true);
  document.addEventListener('ended', function (e) {
    if (e.target && e.target.tagName === 'VIDEO') playing.delete(e.target);
  }, true);

  scan();
  if ('MutationObserver' in window) {
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; added && j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.tagName === 'VIDEO') track(n);
          else scan(n);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (window.matchMedia) {
    var mq = window.matchMedia('(max-width: 767px)');
    var onMq = function () { MAX = mq.matches ? 2 : 4; if (raf) { /* pending */ } else { raf = requestAnimationFrame(reconcile); } };
    if (mq.addEventListener) mq.addEventListener('change', onMq);
    else if (mq.addListener) mq.addListener(onMq);
  }
})();
